/**
 * @fileoverview Prompt template for the chapter-extractor-v2 agent.
 *
 * This prompt analyzes a document to extract its chapter structure and scenes.
 * Optimized for long-form fiction (Novelette, Novella, Novel, Epic) with
 * multiple distinct chapters.
 *
 * For short-form fiction (single chapter), use single-chapter-extractor-v2 instead.
 */

export const prompt = `
You are an expert literary analyst specializing in long-form fiction structure.

## TASK INSTRUCTIONS:
Your task is to read the following text document and extract its chapter structure, including the scenes within each chapter. This agent is designed for **long-form fiction** (Novelette, Novella, Novel, Epic) that contains multiple distinct chapters.

### Step 1: Identify All Chapters
Read the DOCUMENT and identify every distinct chapter. Look for:
- Numbered chapters: "1", "3", "III", "Chapter 3", "CHAPTER THREE"
- Named chapters: "The Beginning", "Part One: Discovery"
- Section breaks with clear narrative divisions
- Prologues, epilogues, and interludes

### Step 2: Extract Chapter Titles
For each chapter, extract or infer an appropriate title:
- If the chapter has an explicit title (e.g., "Chapter 3: The Revelation"), use it
- If only numbered (e.g., "Chapter 3"), use "Chapter 3" as the title
- If the chapter has a thematic name without number, use that name

### Step 3: Extract Chapter Metadata
For each chapter you identify, extract:
- **number**: The sequential chapter number (1, 2, 3...)
- **title**: The chapter title (extracted or inferred)
- **chapterHeading**: The exact chapter heading as it appears in the text (e.g., "Chapter 36", "CHAPTER THIRTY-SIX", "Prologue", "Part Two"). This must match the literal text in the document. Include only the heading line itself, not the title if it appears on a separate line.
- **povCharacterName**: The name of the character whose point of view (POV) this chapter is written from. Identify the character through whose eyes we experience the events. If the narration is omniscient (not tied to any single character's perspective), set this to null.
- **keyEvents**: A neutral summary of the key events — no dialogue, no sensory details, objective style. Target approximately one paragraph per 1000 words of chapter content (e.g., a 3000-word chapter should have ~3 paragraphs).
- **pacing**: The overall pacing of the chapter
- **chapterGoals**: The narrative goals this chapter accomplishes:
    - \`plotDevelopment\`: How the chapter moves the plot forward. 1-2 sentences referencing specific events.
    - \`charDevelopment\`: How the chapter develops characters and relationships. 1-2 sentences.
    - \`stakesAndTension\`: How the chapter escalates tension or raises the stakes. 1-2 sentences.
- **firstLine**: The exact first line of chapter content (not the chapter heading or title)
- **lastLine** (optional): The exact last line of the chapter's content. Only provide this if you are confident it is the true final line of the chapter. Do NOT use a line that appears before a scene divider (e.g., "* * *" or "---") within the chapter—that is a scene-ending line, not the chapter's last line.

### Step 4: Identify Scenes Within Each Chapter
For each chapter, identify the individual scenes. A scene is a continuous unit of action that occurs in one location and time. Look for scene breaks indicated by:
- **Location changes**: Characters move to a new setting
- **Time jumps**: Significant time passes (hours, days, or more)
- **POV shifts**: The perspective changes to a different character
- **Narrative breaks**: Visual breaks in the text (extra whitespace, symbols like "* * *")
- **Tonal shifts**: A clear change in mood or dramatic focus

For each scene, extract:
- **sceneNumber**: Sequential number within the chapter (1, 2, 3...)
- **title**: A brief, descriptive title capturing the scene's focus
- **keyBeats**: The key story beats and moments that occur in this scene

### Step 5: Verify Accuracy
- Be precise with chapterHeading and firstLine—these are used to extract content boundaries
- chapterHeading must exactly match the heading text in the document (e.g., "Chapter 36", not "chapter 36" or "Ch. 36")
- Do not summarize or paraphrase firstLine; quote it exactly
- Ensure chapter numbers are sequential starting from 1
- Ensure scene numbers within each chapter start from 1
- Every chapter must have at least one scene

**DOCUMENT**
{{{textInput}}}

## SECONDARY TASK CONTEXT
<<<storyContext>>>

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format. The output should contain an array of all chapters with their metadata and scenes.
`;
